import React from "react";
import { motion } from "framer-motion";
import { Fingerprint, HeartHandshake, Zap, ShieldCheck } from "lucide-react";

const PILLARS = [
  {
    icon: Fingerprint,
    title: "Personalised Precision",
    desc: "Tailored product picks and custom routines designed exclusively for your hair profile.",
  },
  {
    icon: HeartHandshake,
    title: "100% Inclusive",
    desc: "Bespoke consultations crafted for all textures and types. Styling for anyone, anytime, anywhere.",
  },
  {
    icon: Zap,
    title: "On-Demand Access",
    desc: "Instant, message-based guidance from senior hair stylists exactly when you need it.",
  },
  {
    icon: ShieldCheck,
    title: "Certified Experts",
    desc: "Every stylist is vetted and certified, so the advice you receive is always trustworthy.",
  },
];

const STATS = [
  { value: "100%", label: "Would recommend" },
  { value: "5.0", label: "Average client rating" },
  { value: "15Q", label: "Style DNA intake" },
  { value: "24/7", label: "Message access" },
];

export const WhyUs = () => {
  return (
    <section id="why" className="py-16 md:py-24 bg-stylist-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 ss-grain opacity-[0.06] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <span className="eyebrow text-stylist-rose">Why choose us</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.1]">
              Powered by our unique{" "}
              <span className="italic text-stylist-gold">Style DNA Matrix</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-lg">
              Senior Stylist eliminates guesswork from hair care. Through an interactive
              15-question intake, we analyse your exact hair texture, scalp health and goals to
              pair you with a dedicated certified expert.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 rounded-lg overflow-hidden">
              {STATS.map((s) => (
                <div key={s.label} className="bg-stylist-ink p-6">
                  <div className="font-serif text-3xl md:text-4xl text-stylist-gold">{s.value}</div>
                  <div className="mt-1 text-xs text-white/60 font-mono uppercase tracking-widest">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] hover:border-stylist-gold/40 transition-colors duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-stylist-gold/15 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-stylist-gold" />
                </div>
                <h3 className="mt-4 font-serif text-xl">{p.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
